import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

import { PortfolioNavbar } from '~/components/portfolio/portfolio-navbar.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import { requireAuthenticatedBeforeLoad } from '~/routing/require-authenticated-before-load.ts';
import { getPortfolioLayoutQueryOptions } from '~/server/portfolio/query-options.ts';

export const Route = createFileRoute('/portfolios')({
	beforeLoad: requireAuthenticatedBeforeLoad,
	component: PortfoliosLayout,
	loader: ({ context }) => context.queryClient.ensureQueryData(getPortfolioLayoutQueryOptions()),
});

function PortfoliosLayout() {
	const t = useTranslations();
	const { data: portfolios } = useSuspenseQuery(getPortfolioLayoutQueryOptions());
	const navigate = Route.useNavigate();
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const activePortfolioIdMatch = /^\/portfolios\/(\d+)(?:\/|$)/.exec(pathname);
	const activePortfolioId = activePortfolioIdMatch ? Number(activePortfolioIdMatch[1]) : undefined;

	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (!event.ctrlKey || Number.isNaN(Number(event.key))) {
				return;
			}

			const portfolioIndex = Number(event.key) - 1;
			const portfolio = portfolios?.[portfolioIndex];
			if (!portfolio) {
				return;
			}

			event.preventDefault();
			void navigate({
				params: {
					portfolioId: String(portfolio.id),
				},
				to: '/portfolios/$portfolioId',
			});
		}

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [navigate, portfolios]);

	return (
		<>
			<div className="px-1">
				<PortfolioNavbar activePortfolioId={activePortfolioId} pathname={pathname} portfolios={portfolios} />
			</div>

			{portfolios?.length === 0 ? (
				<div className="flex flex-row gap-x-1 p-2">
					<p className="text-foreground italic">{t('portfolio.empty')}</p>
					<span>😊</span>
				</div>
			) : (
				<Outlet />
			)}
		</>
	);
}
